import { parseAsString, useQueryStates } from 'nuqs';
import { useState, useCallback, useRef, useEffect } from 'react';
import { useLocation } from 'react-router';

import type { DeploymentsPage } from '../Types';
import { getCapacityFilterVariables } from '../utils/capacityFilters';
import { runFiltersParser } from '../utils/runFilters';

import apiRun from '~/api/runtime';

const DEPLOYMENTS_PER_PAGE = 10;

type UseDeploymentDataProps = {
  activeRealm: string;
  environment: string;
  projectById: { [key: string]: Record<string, unknown> } | null;
};

export const useDeploymentData = ({
  activeRealm,
  environment,
  projectById
}: UseDeploymentDataProps) => {
  const [deploymentsList, setDeploymentsList] = useState<DeploymentsPage>();
  const [deploymentsPerPage, setDeploymentsPerPage] =
    useState(DEPLOYMENTS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const abortControllerRef = useRef<AbortController | null>(null);
  const listRequestRef = useRef<AbortController | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [queryParams] = useQueryStates({
    projectId: parseAsString.withDefault(''),
    search: parseAsString.withDefault(''),
    refreshInterval: parseAsString.withDefault('5'),
    filters: runFiltersParser
  });

  const {
    projectId: projectIdParam,
    search: pipelineNameParam,
    refreshInterval: refreshIntervalParam,
    filters: filtersParam
  } = queryParams;

  const getDeployments = useCallback(
    async ({
      page = 0,
      signal,
      showLoading = true
    }: { page?: number; signal?: AbortSignal; showLoading?: boolean } = {}) => {
      if (
        !location.pathname.includes(environment) ||
        Object.keys(projectById || {}).length === 0
      ) {
        return;
      }

      if (showLoading) {
        setLoading(true);
      }
      try {
        const { data } = await apiRun.findPaginated({
          realm: activeRealm,
          environment,
          pipelineName: pipelineNameParam,
          triggers: filtersParam?.triggers || [],
          status: filtersParam?.status || [''],
          ...getCapacityFilterVariables(filtersParam),
          projectName: projectById?.[projectIdParam]?.name || '',
          pageSize: deploymentsPerPage,
          page,
          signal
        });

        if (!signal?.aborted) {
          setDeploymentsList(data.deploymentsPaginated);
        }
      } catch (error) {
        if (signal?.aborted) {
          return;
        }
        throw error;
      } finally {
        if (!signal?.aborted && showLoading) {
          setLoading(false);
        }
      }
    },
    [
      projectById,
      projectIdParam,
      location.pathname,
      environment,
      activeRealm,
      pipelineNameParam,
      filtersParam,
      deploymentsPerPage
    ]
  );

  const setDefaultCurrentPage = useCallback(() => {
    setDeploymentsList(undefined);
    setDeploymentsPerPage(DEPLOYMENTS_PER_PAGE);
    setCurrentPage(0);
  }, []);

  const startListRequest = useCallback(() => {
    listRequestRef.current?.abort();
    listRequestRef.current = new AbortController();
    return listRequestRef.current.signal;
  }, []);

  const onPageChange = useCallback(
    async (page: number) => {
      setDeploymentsPerPage(DEPLOYMENTS_PER_PAGE);
      setCurrentPage(page);
      await getDeployments({ page, signal: startListRequest() });
    },
    [getDeployments, startListRequest]
  );

  const onInfinityScroll = useCallback(() => {
    setDeploymentsPerPage(deploymentsPerPage + DEPLOYMENTS_PER_PAGE);
  }, [deploymentsPerPage]);

  const startInterval = useCallback(() => {
    if (!refreshIntervalParam) return;

    const intervalSeconds = parseInt(refreshIntervalParam, 10);
    if (Number.isNaN(intervalSeconds) || intervalSeconds <= 0) return;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    const intervalMs = intervalSeconds * 1000;
    intervalRef.current = setInterval(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      getDeployments({
        page: currentPage,
        signal: abortControllerRef.current.signal,
        showLoading: false
      });
    }, intervalMs);
  }, [refreshIntervalParam, getDeployments, currentPage]);

  const refreshWithAbort = useCallback(
    (page?: number) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();
      const targetPage = page !== undefined ? page : currentPage;

      startInterval();

      return getDeployments({
        page: targetPage,
        signal: abortControllerRef.current.signal
      });
    },
    [getDeployments, currentPage, startInterval]
  );

  useEffect(() => {
    startInterval();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [startInterval]);

  useEffect(() => {
    setCurrentPage(0);
    getDeployments({ signal: startListRequest() });
  }, [
    projectIdParam,
    pipelineNameParam,
    filtersParam,
    getDeployments,
    projectById,
    startListRequest
  ]);

  useEffect(() => () => listRequestRef.current?.abort(), []);

  return {
    deploymentsList,
    loading,
    DEPLOYMENTS_PER_PAGE,

    refreshWithAbort,
    setDefaultCurrentPage,
    onPageChange,
    onInfinityScroll
  };
};
